import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import swal from "sweetalert2";

import { OrganigramaService } from "./organigrama.service";
import {
  AreaOrg,
  Historial,
  Nodo,
  NodoArbol,
  Persona,
} from "../../shared/model/organigrama/organigrama.model";

/* Lo que el FOR-016 dibuja distinto y el árbol no sabe decir: los dos
   gates van por NOMBRE de puesto porque nada en la base los distingue
   (en Recursos Humanos existe la misma figura estructural y ahí nada
   de esto aplica). Si un puesto se renombra, el dibujo regresa solo al
   árbol normal, sin romperse. */

/** Hijos que no van en la fila de sus hermanos sino colgando del
 *  tronco, a un lado: asesoran a la dirección, no mandan sobre la
 *  fila. La llave es el puesto del PADRE. */
const ASESORES: { [padre: string]: string[] } = {
  "Dirección Desarrollo": ["ODP"],
  // La Asistente de Dirección cuelga del tronco de la DGA por un lado,
  // como en el FOR-016: asiste a la dirección, no manda sobre las
  // direcciones que van en la fila de abajo. Colgada como hija normal
  // se leía como una más de ellas.
  DGA: ["Asistente Dirección"],
};

/** Direcciones cuya fila de líderes lleva la barra del FOR-016 por
 *  debajo, con el equipo del primer líder cruzando la fila entera. */
const PADRES_CON_BANDA_DE_FILA = ["Dirección Desarrollo"];

/** Padres cuyo asesor va AL COSTADO de la caja —misma altura, a la
 *  derecha, unido por una línea horizontal— en vez de colgar del tronco
 *  más abajo. Es como el FOR-016 dibuja a la Asistente de Dirección.
 *  La ODP de Desarrollo NO entra aquí: allá sí cuelga del tronco. */
const ASESORES_AL_COSTADO = ["DGA"];

/**
 * El organigrama, editable.
 *
 * Sustituye al FOR-016 en PDF, que alguien tenía que redibujar cada vez
 * que cambiaba algo. Aquí se arrastra un puesto sobre otro y queda.
 *
 * MANDA SOBRE VACACIONES: mover a alguien de jefe puede cambiar quién le
 * autoriza sus días. La pantalla lo pregunta antes de cada movimiento —
 * nunca lo hace en silencio.
 */
@Component({
  selector: "app-organigrama",
  standalone: true,
  // NgClass va explícito: en standalone, una directiva sin importar no
  // da error de compilación, simplemente no hace nada.
  imports: [CommonModule, FormsModule, MatTooltipModule],
  templateUrl: "./organigrama.component.html",
  styleUrls: ["./organigrama.component.css"],
})
export class OrganigramaComponent implements OnInit, OnDestroy {
  nodos: Nodo[] = [];
  personas: Persona[] = [];
  areas: AreaOrg[] = [];
  puedeEditar = false;

  raiz: NodoArbol | null = null;
  cargando = true;
  error = "";

  /* ---------- Arrastre ---------- */

  /** El puesto que se está arrastrando. */
  arrastrando: number | null = null;

  /** Sobre el que está encima ahora mismo. */
  encima: number | null = null;

  /* ---------- Vista ---------- */

  zoom = 1;
  desplazoX = 0;
  desplazoY = 0;
  private moviendoLienzo = false;
  private origenX = 0;
  private origenY = 0;

  /* ---------- Edición ---------- */

  editando: Nodo | null = null;
  guardando = false;

  /* ---------- Pantalla completa ---------- */

  /**
   * El diagrama a toda la pantalla.
   *
   * Es un overlay fijo y NO el API nativo de fullscreen: los avisos de
   * confirmación (swal) viven en el body, y el fullscreen nativo sólo
   * pinta lo que cuelga del elemento ampliado — un arrastre pediría
   * confirmación en un diálogo invisible. Con el overlay todo sigue en
   * pantalla.
   */
  pantallaCompleta = false;

  /* ---------- Táctil ---------- */

  /** Punteros activos sobre el lienzo: uno arrastra, dos pellizcan. */
  private punteros = new Map<number, { x: number; y: number }>();
  private pinza: { dist: number; zoom: number } | null = null;

  /**
   * En táctil no hay arrastre HTML5: se toca el puesto y luego a su
   * nuevo jefe. Mientras tanto, esto guarda cuál se está moviendo.
   */
  moviendoToque: NodoArbol | null = null;

  /* ---------- Buscar en el diagrama ---------- */

  busqueda = "";

  /* ---------- Historial ---------- */

  historialAbierto = false;
  historial: Historial[] = [];
  cargandoHistorial = false;

  /* ---------- Exportar ---------- */

  /** Mientras exporta, el lienzo se despliega a su tamaño natural. */
  exportando = false;

  @ViewChild("mundo") mundoRef?: ElementRef<HTMLElement>;

  /** Cuando se abre para agregar, de quién va a colgar. */
  private padreNuevo: number | null = null;

  /** Filtro del selector de personas: 44 opciones sin buscar no se usan. */
  filtroPersona = "";

  /**
   * El panel de sugerencias de puesto está abierto.
   *
   * Es un panel propio y no un datalist: el desplegable del datalist es
   * cromo del navegador — sale con el tema del sistema (oscuro, enorme)
   * y no hay CSS que lo alcance.
   */
  sugerenciasAbiertas = false;

  constructor(
    private service: OrganigramaService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando = true;
    this.service.consultar().subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          this.error = res.mensajeError || "No se pudo cargar el organigrama.";
          this.cdr.detectChanges();
          return;
        }
        this.error = "";
        this.nodos = res.nodos || [];
        this.personas = res.personas || [];
        this.areas = res.areas || [];
        this.puestosCatalogo = res.puestos || [];
        this.puedeEditar = !!res.puedeEditar;
        this.raiz = this.armaArbol(this.nodos);
        this.encuadraSiAngosto();
        this.calculaDesalineados();
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.error = "No se pudo cargar el organigrama.";
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Arma el árbol a partir de la lista plana.
   *
   * En dos pasadas y no recursivo desde el servidor: recorrer buscando
   * hijos dentro de una recursión es cuadrático, y con un organigrama
   * que puede crecer no hay razón para pagarlo.
   */
  private armaArbol(planos: Nodo[]): NodoArbol | null {
    const por: { [k: number]: NodoArbol } = {};
    for (const n of planos) {
      if (n.idNodo != null) {
        por[n.idNodo] = { ...n, hijos: [], nivel: 0 };
      }
    }

    // La raíz es la que NO tiene padre, no la que "sobra".
    //
    // Antes se elegía por descarte —cualquier nodo cuyo padre no
    // estuviera en el mapa— y ganaba el último. Con eso, UN solo nodo
    // apuntando a un padre que ya no existe se volvía la raíz y los
    // otros 62 desaparecían de la pantalla, sin error ni aviso: el
    // organigrama simplemente salía casi vacío.
    let raiz: NodoArbol | null = null;
    const sueltos: NodoArbol[] = [];
    for (const n of planos) {
      if (n.idNodo == null) {
        continue;
      }
      const yo = por[n.idNodo];
      if (n.idPadre == null) {
        raiz = yo;
      } else if (por[n.idPadre]) {
        por[n.idPadre].hijos.push(yo);
      } else {
        sueltos.push(yo);
      }
    }

    // Un puesto colgando de un jefe que no está no debe llevarse el
    // diagrama: se cuelga de la raíz para que se vea, y se avisa. El
    // servidor ya no deja crearlos, pero los scripts sí pueden.
    if (sueltos.length && raiz) {
      sueltos.forEach((s) => raiz!.hijos.push(s));
      this.error =
        sueltos.length === 1
          ? "Un puesto depende de un jefe que ya no existe y se muestra colgado de la raíz."
          : sueltos.length +
            " puestos dependen de un jefe que ya no existe y se muestran colgados de la raíz.";
    }

    // El nivel se marca al bajar, para que la pantalla pueda achicar
    // los nodos hondos sin volver a recorrer el árbol.
    const marca = (n: NodoArbol, nivel: number) => {
      n.nivel = nivel;
      n.hijos.sort((a, b) =>
        (a.orden ?? 0) - (b.orden ?? 0) ||
        (a.nombre || a.puesto || "").localeCompare(b.nombre || b.puesto || "")
      );
      n.hijos.forEach((h) => marca(h, nivel + 1));
    };
    if (raiz) {
      marca(raiz, 0);
    }
    return raiz;
  }

  /* ================= Lo que se ve ================= */

  get totalNodos(): number {
    return this.nodos.length;
  }

  get vacantes(): number {
    return this.nodos.filter((n) => !n.idUsuario && !n.nombreLibre).length;
  }

  /**
   * Los que su autorizador de vacaciones NO está en su cadena de mando.
   *
   * No se exige que sea el jefe directo: con la estructura real del
   * FOR-016, a un desarrollador lo autoriza el director —su abuelo en
   * el diagrama— y eso es correcto, no una alerta. La alerta es cuando
   * el autorizador no aparece en NINGÚN nivel hacia arriba: entonces sí
   * alguien lo cambió por fuera.
   *
   * Se calcula una vez al cargar, no en cada repintado: subir la cadena
   * de 63 nodos en cada ciclo de detección de cambios es pagar de más.
   */
  private idsDesalineados = new Set<number>();

  private calculaDesalineados(): void {
    const porNodo: { [k: number]: Nodo } = {};
    for (const n of this.nodos) {
      if (n.idNodo != null) {
        porNodo[n.idNodo] = n;
      }
    }

    this.idsDesalineados = new Set<number>();
    for (const n of this.nodos) {
      if (!n.idUsuario || n.idPadre == null || n.idAutorizaVacaciones == null) {
        continue;
      }
      let actual: Nodo | undefined = porNodo[n.idPadre];
      let enCadena = false;
      let guarda = 0;
      while (actual && guarda++ < 100) {
        if (actual.idUsuario === n.idAutorizaVacaciones) {
          enCadena = true;
          break;
        }
        actual = actual.idPadre != null ? porNodo[actual.idPadre] : undefined;
      }
      if (!enCadena && n.idNodo != null) {
        this.idsDesalineados.add(n.idNodo);
      }
    }
  }

  get desalineados(): number {
    return this.idsDesalineados.size;
  }

  /** Para marcar el nodo en pantalla. */
  desalineado(n: NodoArbol): boolean {
    return n.idNodo != null && this.idsDesalineados.has(n.idNodo);
  }

  /* ================= Colores por área ================= */

  private readonly TONOS = [
    "#4361A4", "#B04A42", "#2E7F78", "#9A6B2F",
    "#6E549B", "#A85278", "#3E7A4E", "#8A5C3E",
  ];

  /** El color del área. Sale del id: renombrarla no lo cambia. */
  colorArea(n: Nodo): string {
    if (n.idArea == null) {
      return "#6A7A88";
    }
    return this.TONOS[n.idArea % this.TONOS.length];
  }

  tinte(hex: string, alfa: number): string {
    const h = (hex || "#4361A4").replace("#", "");
    return `rgba(${Number.parseInt(h.substring(0, 2), 16)}, ${Number.parseInt(
      h.substring(2, 4),
      16
    )}, ${Number.parseInt(h.substring(4, 6), 16)}, ${alfa})`;
  }

  /** Iniciales, para el círculo del nodo. */
  iniciales(n: Nodo): string {
    const t = (n.nombre || n.nombreLibre || "").trim();
    if (!t) {
      return "·";
    }
    const p = t.split(/\s+/);
    return (p[0][0] + (p.length > 1 ? p[1][0] : "")).toUpperCase();
  }

  /* ================= Arrastrar para recolgar ================= */

  empiezaArrastre(n: NodoArbol, ev: DragEvent): void {
    if (!this.puedeEditar || n.idPadre == null) {
      // La raíz no se mueve: no hay de quién colgarla.
      ev.preventDefault();
      return;
    }
    this.arrastrando = n.idNodo ?? null;
    ev.dataTransfer?.setData("text/plain", String(n.idNodo));
    if (ev.dataTransfer) {
      ev.dataTransfer.effectAllowed = "move";
    }
  }

  sobre(n: NodoArbol, ev: DragEvent): void {
    if (!this.puedeEditar || this.arrastrando == null) {
      return;
    }
    // Sin esto, el navegador no permite soltar.
    ev.preventDefault();
    if (n.idNodo !== this.arrastrando && !this.esDescendiente(n, this.arrastrando)) {
      this.encima = n.idNodo ?? null;
    }
  }

  sale(): void {
    this.encima = null;
  }

  terminaArrastre(): void {
    this.arrastrando = null;
    this.encima = null;
  }

  /**
   * Verdadero si `posible` cuelga del nodo que se arrastra.
   *
   * Se comprueba aquí además del servidor para no marcar como destino
   * válido algo que va a ser rechazado: soltar y recibir un error es
   * peor que no poder soltar.
   */
  private esDescendiente(posible: NodoArbol, idAncestro: number): boolean {
    let actual: Nodo | undefined = posible;
    let guarda = 0;
    while (actual && guarda++ < 200) {
      if (actual.idNodo === idAncestro) {
        return true;
      }
      actual = this.nodos.find((x) => x.idNodo === actual!.idPadre);
    }
    return false;
  }

  suelta(destino: NodoArbol, ev: DragEvent): void {
    ev.preventDefault();
    const idNodo = this.arrastrando;
    this.terminaArrastre();

    if (idNodo == null || destino.idNodo == null || idNodo === destino.idNodo) {
      return;
    }
    this.confirmaMovimiento(idNodo, destino);
  }

  /* ---- Mover por toques (táctil) ---- */

  /** Primer toque: el puesto que se va a mover. */
  empiezaMoverToque(n: NodoArbol, ev: Event): void {
    ev.stopPropagation();
    if (!this.puedeEditar || n.idPadre == null || n.idNodo == null) {
      return;
    }
    this.moviendoToque = n;
    // El mismo atenuado que el arrastre: se ve cuál se está moviendo.
    this.arrastrando = n.idNodo;
  }

  cancelaMoverToque(): void {
    this.moviendoToque = null;
    this.arrastrando = null;
    this.encima = null;
  }

  /** Segundo toque: el nuevo jefe. Sin movimiento en curso, no hace nada. */
  tocaNodo(n: NodoArbol, ev: Event): void {
    const origen = this.moviendoToque;
    if (!origen || origen.idNodo == null) {
      return;
    }
    // Los botones del nodo tienen su propio clic.
    if ((ev.target as HTMLElement).closest(".org-nodo__acc")) {
      return;
    }
    if (n.idNodo === origen.idNodo || this.esDescendiente(n, origen.idNodo)) {
      swal.fire({
        text: "Un puesto no puede colgar de sí mismo ni de alguien que depende de él.",
        icon: "info",
        confirmButtonText: "Entendido",
      });
      return;
    }
    this.cancelaMoverToque();
    this.confirmaMovimiento(origen.idNodo, n);
  }

  /** La confirmación del movimiento, común al arrastre y al toque. */
  private confirmaMovimiento(idNodo: number, destino: NodoArbol): void {
    const nodo = this.nodos.find((n) => n.idNodo === idNodo);
    if (!nodo || destino.idNodo == null || nodo.idPadre === destino.idNodo) {
      return;
    }
    const quien = nodo.nombre || nodo.nombreLibre || nodo.puesto || "ese puesto";
    const jefe = destino.nombre || destino.nombreLibre || destino.puesto || "ese puesto";

    // Sólo tiene efecto sobre vacaciones si las dos puntas tienen
    // persona: un puesto vacante no autoriza los días de nadie.
    const afectaVacaciones = !!nodo.idUsuario && !!destino.idUsuario;

    swal
      .fire({
        title: "¿Moverlo?",
        html: afectaVacaciones
          ? `<b>${quien}</b> pasa a depender de <b>${jefe}</b>.` +
            `<br><br>Esto también cambia quién le autoriza sus vacaciones.`
          : `<b>${quien}</b> pasa a depender de <b>${jefe}</b>.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: afectaVacaciones ? "Mover y cambiar autorizador" : "Mover",
        cancelButtonText: "Cancelar",
        // Cuando toca vacaciones se ofrece la salida de mover sin
        // arrastrarlas: hay casos —una reestructura por dibujar— en que
        // el diagrama va antes que el permiso.
        showDenyButton: afectaVacaciones,
        denyButtonText: "Mover sin cambiarlo",
      })
      .then((r) => {
        if (!r.isConfirmed && !r.isDenied) {
          return;
        }
        this.mueve(idNodo, destino.idNodo!, r.isConfirmed && afectaVacaciones);
      });
  }

  private mueve(idNodo: number, idPadre: number, sincroniza: boolean): void {
    this.service.mover(idNodo, idPadre, sincroniza).subscribe({
      next: (res) => {
        if (res.codigoError) {
          swal.fire("No se movió", res.mensajeError || "Intenta de nuevo.", "error");
          return;
        }
        this.cargar();
      },
      error: () => swal.fire("No se movió", "No se pudo mover el puesto.", "error"),
    });
  }

  /* ================= Alta, edición y baja ================= */

  nuevo(padre: NodoArbol): void {
    this.filtroPersona = "";
    this.padreNuevo = padre.idNodo ?? null;
    this.editando = {
      idPadre: this.padreNuevo,
      puesto: "",
      idUsuario: null,
      nombreLibre: "",
      idArea: padre.idArea ?? null,
      orden: (padre.hijos.length + 1) * 10,
    };
  }

  editar(n: NodoArbol): void {
    // Copia propia: sin ella, escribir en el formulario cambiaría el
    // nodo del diagrama en vivo y "Cancelar" no revertiría nada.
    const { hijos, nivel, ...limpio } = n;
    this.editando = { ...limpio };
  }

  cancelar(): void {
    this.editando = null;
    this.padreNuevo = null;
    this.filtroPersona = "";
  }

  guardar(): void {
    if (!this.editando) {
      return;
    }
    if (!(this.editando.puesto || "").trim()) {
      swal.fire("Falta el puesto", "Ponle el nombre del puesto.", "warning");
      return;
    }

    this.guardando = true;
    this.service.guardar(this.editando).subscribe({
      next: (res) => {
        this.guardando = false;
        if (res.codigoError) {
          swal.fire("No se guardó", res.mensajeError || "Intenta de nuevo.", "error");
          this.cdr.detectChanges();
          return;
        }
        this.editando = null;
        this.padreNuevo = null;
        this.cargar();
      },
      error: () => {
        this.guardando = false;
        swal.fire("No se guardó", "No se pudo guardar el puesto.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  eliminar(n: NodoArbol): void {
    const cuantos = n.hijos.length;
    const quien = n.nombre || n.nombreLibre || n.puesto;

    swal
      .fire({
        title: "¿Eliminar este puesto?",
        html: cuantos
          ? `<b>${quien}</b><br><br>Los ${cuantos} puestos que dependen de él ` +
            `pasarán a su jefe, no se eliminan.`
          : `<b>${quien}</b>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#b04a42",
      })
      .then((r) => {
        if (!r.isConfirmed || n.idNodo == null) {
          return;
        }
        this.service.eliminar(n.idNodo).subscribe({
          next: (res) => {
            if (res.codigoError) {
              swal.fire("No se eliminó", res.mensajeError || "", "error");
              return;
            }
            this.cargar();
          },
          error: () => swal.fire("No se eliminó", "Intenta de nuevo.", "error"),
        });
      });
  }

  /** Las personas que se pueden asignar: las libres más la actual. */
  get personasDisponibles(): Persona[] {
    const actual = this.editando?.idUsuario;
    return this.personas.filter((p) => !p.asignada || p.idUsuario === actual);
  }

  /** Las disponibles que además pasan el filtro de búsqueda. */
  get personasFiltradas(): Persona[] {
    const busca = this.filtroPersona.trim().toLowerCase();
    if (!busca) {
      return this.personasDisponibles;
    }
    // La persona ya elegida se queda aunque no coincida: si el filtro
    // la sacara de las opciones, el select la soltaría en silencio.
    const actual = this.editando?.idUsuario;
    return this.personasDisponibles.filter(
      (p) =>
        p.idUsuario === actual ||
        (p.nombre || "").toLowerCase().includes(busca) ||
        (p.usuario || "").toLowerCase().includes(busca) ||
        (p.puesto || "").toLowerCase().includes(busca)
    );
  }

  /** De quién va a colgar el puesto nuevo, para decirlo en el modal. */
  get nombrePadreNuevo(): string {
    const p = this.nodos.find((n) => n.idNodo === this.padreNuevo);
    if (!p) {
      return "";
    }
    const quien = p.nombre || p.nombreLibre || "";
    return quien ? `${quien} — ${p.puesto || ""}` : p.puesto || "";
  }

  /** El catalogo real de puestos (adm_puesto), tal como llega. */
  puestosCatalogo: string[] = [];

  /**
   * Las sugerencias del campo Puesto: el catalogo de adm_puesto — los
   * puestos reales de la empresa. Si el backend aun no lo manda (falta
   * recompilar), se cae a los ya usados en el diagrama para que el
   * campo no se quede sin sugerencias.
   */
  get puestosConocidos(): string[] {
    if (this.puestosCatalogo.length) {
      return this.puestosCatalogo;
    }
    const vistos = new Set<string>();
    for (const n of this.nodos) {
      const p = (n.puesto || "").trim();
      if (p) {
        vistos.add(p);
      }
    }
    return [...vistos].sort((a, b) => Number(a > b) - Number(a < b));
  }

  /** Las sugerencias que se pintan: filtradas por lo escrito. */
  get puestosSugeridos(): string[] {
    const escrito = (this.editando?.puesto || "").trim().toLowerCase();
    const todos = this.puestosConocidos;
    if (!escrito) {
      return todos;
    }
    const filtrados = todos.filter((p) => p.toLowerCase().includes(escrito));
    // Si lo escrito ya es exactamente una opción, no hay nada que
    // sugerir: un panel con la misma palabra sólo estorba.
    if (filtrados.length === 1 && filtrados[0].toLowerCase() === escrito) {
      return [];
    }
    return filtrados;
  }

  /**
   * Elegir con mousedown y no con click: el mousedown corre ANTES del
   * blur del input — con click, el blur cerraba el panel y el clic
   * caía en el vacío.
   */
  eligePuesto(p: string, ev: Event): void {
    ev.preventDefault();
    if (this.editando) {
      this.editando.puesto = p;
    }
    this.sugerenciasAbiertas = false;
  }

  /* ================= Mover y acercar el lienzo ================= */

  acerca(paso: number): void {
    this.zoom = this.limitaZoom(this.zoom + paso);
  }

  /**
   * Zoom con la rueda, directo sobre el diagrama.
   *
   * El lienzo no tiene scroll propio, así que la rueda ahí no le
   * quita el desplazamiento a nadie — y en pantalla completa es la
   * única forma cómoda de acercarse a una rama.
   */
  rueda(ev: WheelEvent): void {
    ev.preventDefault();
    this.acerca(ev.deltaY < 0 ? 0.1 : -0.1);
  }

  encuadra(): void {
    this.zoom = 1;
    this.desplazoX = 0;
    this.desplazoY = 0;

    // En una pantalla angosta, un solo nodo (208 px) ya llena el
    // lienzo: se ajusta el zoom para que el árbol quepa a lo ancho.
    // En escritorio se deja en 1, como siempre.
    const lienzo = this.host.nativeElement.querySelector(".org__lienzo") as HTMLElement | null;
    const raiz = this.host.nativeElement.querySelector(".org__mundo .org-rama") as HTMLElement | null;
    if (!lienzo || !raiz || lienzo.clientWidth >= 700) {
      return;
    }
    // offsetWidth es el ancho de maquetado, sin transform: sirve
    // aunque el zoom anterior siga pintado.
    const ancho = raiz.offsetWidth;
    if (ancho > 0) {
      this.zoom = this.limitaZoom((lienzo.clientWidth - 24) / ancho);
    }
  }

  /** Tras cargar, y sólo en pantalla angosta: el encuadre automático. */
  private encuadraSiAngosto(): void {
    setTimeout(() => {
      const lienzo = this.host.nativeElement.querySelector(".org__lienzo") as HTMLElement | null;
      if (lienzo && lienzo.clientWidth < 700) {
        this.encuadra();
        this.cdr.detectChanges();
      }
    }, 0);
  }

  private limitaZoom(z: number): number {
    return Math.min(1.6, Math.max(0.15, +z.toFixed(2)));
  }

  /*
   * Pan y pinza con Pointer Events: el mouse y el dedo llegan por el
   * mismo camino. Con mouse* a secas, un dedo arrastrando no movía
   * nada (el navegador se quedaba el gesto como scroll) y no había
   * forma de acercar: en un teléfono no existe la rueda.
   */
  tomaLienzo(ev: PointerEvent): void {
    // Sólo con el fondo: arrancando sobre un nodo, el arrastre de
    // recolgar y el del lienzo se pelearían.
    if ((ev.target as HTMLElement).closest(".org-nodo")) {
      return;
    }
    // Sin esto, el navegador toma el mousedown sobre el fondo como el
    // inicio de una selección de texto y, al mover, va pintando de azul
    // todas las cajas por las que pasa el puntero. Cancelar el
    // pointerdown cancela también esa selección.
    ev.preventDefault();
    (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
    this.punteros.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (this.punteros.size === 2) {
      // Dos dedos: pinza. El arrastre de uno se suspende.
      this.pinza = { dist: this.distanciaPunteros(), zoom: this.zoom };
      this.moviendoLienzo = false;
      return;
    }
    this.moviendoLienzo = true;
    this.origenX = ev.clientX - this.desplazoX;
    this.origenY = ev.clientY - this.desplazoY;
  }

  mueveLienzo(ev: PointerEvent): void {
    if (!this.punteros.has(ev.pointerId)) {
      return;
    }
    this.punteros.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (this.pinza && this.punteros.size >= 2) {
      const d = this.distanciaPunteros();
      if (this.pinza.dist > 0) {
        this.zoom = this.limitaZoom(this.pinza.zoom * (d / this.pinza.dist));
      }
      return;
    }
    if (!this.moviendoLienzo) {
      return;
    }
    this.desplazoX = ev.clientX - this.origenX;
    this.desplazoY = ev.clientY - this.origenY;
  }

  sueltaLienzo(ev?: PointerEvent): void {
    if (ev) {
      this.punteros.delete(ev.pointerId);
    } else {
      this.punteros.clear();
    }
    if (this.punteros.size < 2) {
      this.pinza = null;
    }
    if (this.punteros.size === 0) {
      this.moviendoLienzo = false;
      return;
    }
    if (this.punteros.size === 1) {
      // Quedó un dedo: retoma el arrastre desde donde está, sin salto.
      const p = Array.from(this.punteros.values())[0];
      this.moviendoLienzo = true;
      this.origenX = p.x - this.desplazoX;
      this.origenY = p.y - this.desplazoY;
    }
  }

  private distanciaPunteros(): number {
    const ps = Array.from(this.punteros.values());
    if (ps.length < 2) {
      return 0;
    }
    return Math.hypot(ps[0].x - ps[1].x, ps[0].y - ps[1].y);
  }

  alternaPantalla(): void {
    this.pantallaCompleta = !this.pantallaCompleta;
    this.fijaCuerpo(this.pantallaCompleta);
  }

  @HostListener("document:keydown.escape")
  cierraConEscape(): void {
    // Con un modal abierto, el Escape es para él, no para salir.
    if (this.pantallaCompleta && !this.editando) {
      this.pantallaCompleta = false;
      this.fijaCuerpo(false);
      this.cdr.detectChanges();
    }
  }

  /**
   * En pantalla completa el body no debe desplazarse: en un teléfono
   * los gestos que el lienzo no captura harían scroll de la página de
   * fondo y el navegador escondería la barra a media interacción.
   * La clase ya existe en styles.css (la usa la matriz de riesgos).
   */
  private fijaCuerpo(fijo: boolean): void {
    document.body.classList.toggle("mc-cuerpo-fijo", fijo);
  }

  ngOnDestroy(): void {
    this.fijaCuerpo(false);
  }

  /** El aviso ámbar, explicado con un toque: el tooltip no existe en táctil. */
  explicaDesalineado(n?: NodoArbol): void {
    const quien = n ? n.nombre || n.nombreLibre || n.puesto || "Esta persona" : null;
    swal.fire({
      title: "Autorizador distinto",
      text: quien
        ? `A ${quien} le autoriza vacaciones alguien que no es su jefe en este diagrama. ` +
          "Alguien lo cambió por fuera. Se corrige moviendo la caja o ajustando el saldo."
        : "Su autorizador de vacaciones no es su jefe en el diagrama. Alguien lo cambió por fuera.",
      icon: "info",
      confirmButtonText: "Entendido",
    });
  }

  /**
   * Verdadero si todos los hijos son hoja: se apilan en lista vertical,
   * como en el FOR-016. Catorce desarrolladores en fila horizontal
   * harían el diagrama del ancho de tres pantallas; apilados, la rama
   * mide una columna.
   */
  esLista(n: NodoArbol): boolean {
    const fila = this.hijosDeFila(n);
    return fila.length > 1 && fila.every((h) => !h.hijos.length);
  }

  /** Verdadero si el FOR-016 saca a este hijo de la fila (la ODP). */
  private esAsesor(h: NodoArbol, padre: NodoArbol): boolean {
    const marca = ASESORES[(padre.puesto || "").trim()];
    return !!marca && marca.indexOf((h.puesto || "").trim()) >= 0;
  }

  /** Los hijos que cuelgan del tronco a un lado, fuera de la fila. */
  asesoresDe(n: NodoArbol): NodoArbol[] {
    return n.hijos.filter((h) => this.esAsesor(h, n));
  }

  /**
   * Verdadero si el asesor de n va al costado de su caja y no colgando
   * del tronco. Sólo la Asistente de Dirección, como en el FOR-016.
   */
  asesorAlCostado(n: NodoArbol): boolean {
    return ASESORES_AL_COSTADO.indexOf((n.puesto || "").trim()) >= 0;
  }

  /** Los hijos que sí forman la fila de hermanos. */
  hijosDeFila(n: NodoArbol): NodoArbol[] {
    return n.hijos.filter((h) => !this.esAsesor(h, n));
  }

  /**
   * Verdadero si la fila de hijos de n lleva la barra del FOR-016.
   * La plantilla pone entonces --lideres en el contenedor de la fila;
   * las variables CSS se heredan, así que el número baja solo hasta la
   * banda del equipo, que vive un nivel más adentro.
   */
  filaConBanda(n: NodoArbol): boolean {
    return PADRES_CON_BANDA_DE_FILA.indexOf((n.puesto || "").trim()) >= 0;
  }

  /**
   * Verdadero si la banda de n (un líder con equipo en lista) se
   * dibuja cruzando la fila de sus hermanos, colgada de la barra.
   */
  bandaDeFila(n: NodoArbol): boolean {
    if (n.idPadre == null) {
      return false;
    }
    const p = this.nodos.find((x) => x.idNodo === n.idPadre);
    return !!p && PADRES_CON_BANDA_DE_FILA.indexOf((p.puesto || "").trim()) >= 0;
  }

  /**
   * Los hijos de una lista, en RENGLONES por puesto.
   *
   * Es la banda del FOR-016: una fila de Desarrollador Sr, una de Med,
   * una de Jr — cada antigüedad en su renglón colgando de la misma
   * espina. Un puesto único queda como renglón de una sola caja, así
   * que los equipos chicos con puestos distintos siguen apilándose
   * hacia abajo igual que antes.
   */
  gruposDe(n: NodoArbol): { puesto: string; items: NodoArbol[] }[] {
    const grupos: { puesto: string; items: NodoArbol[] }[] = [];
    for (const h of this.hijosDeFila(n)) {
      const clave = (h.puesto || "").trim();
      const g = grupos.find((x) => x.puesto === clave);
      if (g) {
        g.items.push(h);
      } else {
        grupos.push({ puesto: clave, items: [h] });
      }
    }
    return grupos;
  }

  trackGrupo = (_: number, g: { puesto: string }) => g.puesto;


  /* ================= Buscar ================= */

  /** Los nodos que coinciden con lo escrito. */
  get idsHallados(): Set<number> {
    const busca = this.busqueda.trim().toLowerCase();
    const ids = new Set<number>();
    if (!busca) {
      return ids;
    }
    for (const n of this.nodos) {
      if (
        (n.nombre || "").toLowerCase().includes(busca) ||
        (n.nombreLibre || "").toLowerCase().includes(busca) ||
        (n.usuario || "").toLowerCase().includes(busca) ||
        (n.puesto || "").toLowerCase().includes(busca)
      ) {
        if (n.idNodo != null) {
          ids.add(n.idNodo);
        }
      }
    }
    return ids;
  }

  hallado(n: NodoArbol): boolean {
    return n.idNodo != null && this.idsHallados.has(n.idNodo);
  }

  /**
   * Centra el primer hallazgo en el lienzo.
   *
   * El lienzo no tiene scroll — se mueve con translate — así que
   * centrar es medir dónde quedó la caja en pantalla y correr el
   * desplazamiento por esa diferencia. Funciona a cualquier zoom
   * porque el translate se aplica antes de la escala.
   */
  centraHallado(): void {
    const caja = this.host.nativeElement.querySelector(".org-nodo.es-hallado");
    const lienzo = this.host.nativeElement.querySelector(".org__lienzo");
    if (!caja || !lienzo) {
      return;
    }
    const rc = (caja as HTMLElement).getBoundingClientRect();
    const rl = (lienzo as HTMLElement).getBoundingClientRect();
    this.desplazoX += rl.left + rl.width / 2 - (rc.left + rc.width / 2);
    this.desplazoY += rl.top + rl.height / 3 - (rc.top + rc.height / 2);
  }

  /* ================= Historial ================= */

  abreHistorial(): void {
    this.historialAbierto = true;
    this.cargandoHistorial = true;
    this.service.historial().subscribe({
      next: (res) => {
        this.cargandoHistorial = false;
        this.historial = res.historial || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargandoHistorial = false;
        this.historial = [];
        this.cdr.detectChanges();
      },
    });
  }

  colorAccion(a?: string): string {
    switch (a) {
      case "ALTA": return "#2e7d52";
      case "BAJA": return "#b04a42";
      case "MOVIMIENTO": return "#3e6ca3";
      default: return "#8a5a1f";
    }
  }

  /* ================= Exportar a PDF ================= */

  /**
   * El FOR-016 desde la pantalla: es lo que evita que el diagrama vivo
   * y el PDF oficial vuelvan a divergir, como pasó con la v1.27.
   */
  exportarPdf(): void {
    swal
      .fire({
        title: "Exportar FOR-016",
        text: "Versión que llevará el encabezado del formato:",
        input: "text",
        inputValue: "1.28",
        showCancelButton: true,
        confirmButtonText: "Generar PDF",
        cancelButtonText: "Cancelar",
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.generaPdf((r.value || "1.28").trim());
      });
  }

  private generaPdf(version: string): void {
    const mundo = this.mundoRef?.nativeElement;
    if (!mundo) {
      return;
    }

    // El lienzo se despliega a su tamaño natural: capturar con el
    // recorte del overflow y el zoom aplicado sacaría solo la ventana
    // visible, no el organigrama.
    this.exportando = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      // En un teléfono, un canvas al doble de un árbol de miles de
      // píxeles pasa el tope de Safari y sale en blanco sin avisar.
      const escala = window.innerWidth < 600 ? 1 : 2;
      html2canvas(mundo, { backgroundColor: "#ffffff", scale: escala })
        .then((canvas) => {
          const img = canvas.toDataURL("image/png");
          // Página a la medida del diagrama, como el FOR-016 original
          // (que es una sola hoja enorme), más espacio de encabezado.
          const w = canvas.width / escala;
          const h = canvas.height / escala;
          const pdf = new jsPDF({
            orientation: w > h ? "landscape" : "portrait",
            unit: "px",
            format: [w + 48, h + 96],
          });

          const hoy = new Date().toLocaleDateString("es-MX", {
            day: "2-digit", month: "2-digit", year: "numeric",
          });
          pdf.setFontSize(11);
          pdf.setTextColor(40, 40, 40);
          pdf.text("SOLSER · Estructura Organizacional", 24, 30);
          pdf.setFontSize(9);
          pdf.setTextColor(110, 110, 110);
          pdf.text(
            "FOR-016 Organigrama <V " + version + "> Fecha de Liberación <" + hoy + ">",
            w + 24, 30, { align: "right" }
          );
          pdf.text("Generado desde COS · Centro Operativo Solser", 24, 46);
          pdf.addImage(img, "PNG", 24, 64, w, h);
          pdf.save("FOR-016 Organigrama v" + version + ".pdf");
        })
        .catch(() => {
          swal.fire({
            text: "No se pudo generar el PDF. Inténtalo desde una computadora.",
            icon: "error",
            confirmButtonText: "Entendido",
          });
        })
        .finally(() => {
          this.exportando = false;
          this.cdr.detectChanges();
        });
    }, 80);
  }

  trackNodo = (_: number, n: NodoArbol) => n.idNodo;
  trackHist = (_: number, h: Historial) => h.idHistorial;
}
